/**
 * Google Calendar (Faza 5, PLAN §10): status połączenia, wybór kalendarzy do
 * synchronizacji, mapowanie kalendarz -> przestrzeń, „Synchronizuj teraz”.
 *
 * „Połącz” to zwykły link — flow OAuth wymaga pełnej nawigacji przeglądarki
 * (fetch nie może podążyć za redirectem na accounts.google.com). Po powrocie
 * backend przekierowuje tu z `?calendar=connected|error`.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { CALENDAR_OAUTH_START, api } from "../api";
import { useSpaces } from "../lists/hooks";
import { settingsKeys } from "./SettingsPage";
import type { CalendarSyncResult } from "./types";

export function CalendarSection() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [notice, setNotice] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const status = useQuery({ queryKey: settingsKeys.calendar, queryFn: api.calendarStatus });
  const spaces = useSpaces();
  const connected = status.data?.connected ?? false;

  // Lista kalendarzy konta ma sens dopiero po połączeniu.
  const calendars = useQuery({
    queryKey: settingsKeys.googleCalendars,
    queryFn: api.googleCalendars,
    enabled: connected,
    retry: false,
  });

  useEffect(() => {
    const result = params.get("calendar");
    if (!result) return;
    if (result === "connected") setNotice(`Połączono konto ${params.get("account") ?? ""}`.trim());
    else setProblem(params.get("detail") ?? "Nie udało się połączyć z Google.");
    params.delete("calendar");
    params.delete("detail");
    params.delete("account");
    setParams(params, { replace: true });
    void qc.invalidateQueries({ queryKey: settingsKeys.calendar });
  }, [params, setParams, qc]);

  function refresh() {
    void qc.invalidateQueries({ queryKey: settingsKeys.calendar });
    void qc.invalidateQueries({ queryKey: ["calendar-events"] });
  }

  const saveConfig = useMutation({
    mutationFn: api.calendarConfig,
    onSuccess: (next) => {
      qc.setQueryData(settingsKeys.calendar, next);
      refresh();
    },
    onError: (e: Error) => setProblem(e.message),
  });

  const sync = useMutation({
    mutationFn: api.calendarSync,
    onSuccess: (result: CalendarSyncResult) => {
      setNotice(
        `Zsynchronizowano: +${result.created} nowych, ${result.updated} bez zmian, ` +
          `-${result.deleted} usuniętych.`,
      );
      setProblem(result.errors.length ? result.errors.join("; ") : null);
      refresh();
    },
    onError: (e: Error) => setProblem(e.message),
  });

  const disconnect = useMutation({
    mutationFn: api.calendarDisconnect,
    onSuccess: () => {
      setNotice("Odłączono konto Google, cache wyczyszczony.");
      refresh();
    },
  });

  const selected = status.data?.calendars ?? [];
  const spaceMap = status.data?.space_map ?? {};

  /** `primary` to alias głównego kalendarza — przy odznaczaniu trzeba zdjąć oba. */
  function toggleCalendar(id: string, isPrimary: boolean, on: boolean) {
    const aliases = isPrimary ? [id, "primary"] : [id];
    const next = on
      ? [...new Set([...selected, id])]
      : selected.filter((c) => !aliases.includes(c));
    saveConfig.mutate({ calendars: next.length ? next : ["primary"] });
  }

  function mapSpace(id: string, slug: string) {
    saveConfig.mutate({ space_map: { ...spaceMap, [id]: slug || null } });
  }

  return (
    <section className="card settings__section">
      <h2 className="card__title">Kalendarz Google</h2>
      <p className="muted">
        Kalendarz to twardy krajobraz — agent tylko go czyta, a zapisuje wyłącznie po Twoim
        potwierdzeniu. Backend trzyma cache ±{status.data?.window_days ?? 14} dni i odświeża go co{" "}
        {status.data?.sync_minutes ?? 15} min.
      </p>

      {status.isLoading && <p className="muted">Ładowanie…</p>}
      {notice && <p className="muted">{notice}</p>}
      {problem && <p className="error">{problem}</p>}

      {status.data && !status.data.configured && (
        <p className="error">
          Backend nie ma danych klienta OAuth. Uzupełnij <code>GOOGLE_CLIENT_ID</code> i{" "}
          <code>GOOGLE_CLIENT_SECRET</code> w <code>.env</code> (instrukcja w{" "}
          <code>.env.example</code>).
        </p>
      )}

      {status.data && (
        <p className="muted">
          {connected
            ? `Połączono: ${status.data.account ?? "?"} · w cache ${status.data.cached_events} wydarzeń` +
              (status.data.last_sync
                ? ` · ostatni sync ${new Date(status.data.last_sync).toLocaleString("pl-PL")}`
                : "")
            : "Niepołączono."}
        </p>
      )}

      <div className="clarify__buttons">
        {!connected &&
          (status.data?.configured ? (
            <a className="btn btn--primary" href={CALENDAR_OAUTH_START}>
              Połącz z Google
            </a>
          ) : (
            <button className="btn btn--primary" disabled>
              Połącz z Google
            </button>
          ))}
        {connected && (
          <>
            <button
              className="btn btn--primary"
              disabled={sync.isPending}
              onClick={() => sync.mutate()}
            >
              {sync.isPending ? "Synchronizuję…" : "Synchronizuj teraz"}
            </button>
            <button
              className="btn btn--danger"
              disabled={disconnect.isPending}
              onClick={() => disconnect.mutate()}
            >
              Rozłącz
            </button>
          </>
        )}
      </div>

      {connected && (
        <>
          <h3 className="group__title">Kalendarze do synchronizacji</h3>
          {calendars.isLoading && <p className="muted">Pobieram listę kalendarzy…</p>}
          {calendars.error && (
            <p className="error">Nie udało się pobrać listy kalendarzy z Google.</p>
          )}
          <ul className="rows">
            {(calendars.data ?? []).map((cal) => (
              <li className="row" key={cal.id}>
                <label className="row__check">
                  <input
                    type="checkbox"
                    checked={selected.includes(cal.id) || (cal.primary && selected.includes("primary"))}
                    onChange={(e) => toggleCalendar(cal.id, cal.primary, e.target.checked)}
                    aria-label={`Synchronizuj ${cal.summary}`}
                  />
                </label>
                <div className="row__main">
                  <span className="row__title">{cal.summary}</span>
                  <span className="row__meta">
                    <span>{cal.id}</span>
                    {cal.primary && <span className="badge">główny</span>}
                  </span>
                </div>
                <div className="row__actions">
                  <select
                    className="field__input"
                    value={spaceMap[cal.id] ?? ""}
                    onChange={(e) => mapSpace(cal.id, e.target.value)}
                    aria-label={`Przestrzeń dla ${cal.summary}`}
                  >
                    <option value="">wspólny</option>
                    {(spaces.data ?? []).map((space) => (
                      <option key={space.slug} value={space.slug}>
                        {space.name}
                      </option>
                    ))}
                  </select>
                </div>
              </li>
            ))}
          </ul>
          <p className="muted">
            Kalendarz bez przypisanej przestrzeni jest wspólny — widać go w każdym filtrze.
          </p>
        </>
      )}
    </section>
  );
}
